
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Flag } from 'lucide-react';
import { useCountryLeadCounts } from '@/hooks/useCountryLeadCounts';

interface TopCountriesListProps {
  userId?: string;
  countries?: Record<string, number>;
}

const COLORS = [
  'bg-blue-500',
  'bg-indigo-500',
  'bg-violet-500',
  'bg-purple-500',
  'bg-fuchsia-500',
  'bg-pink-500',
  'bg-rose-400',
  'bg-orange-400',
  'bg-amber-400',
  'bg-teal-400',
];

export const TopCountriesList: React.FC<TopCountriesListProps> = ({ userId, countries }) => {
  const hookResult = useCountryLeadCounts(countries ? undefined : userId);

  const sourceData = countries || hookResult.data;
  const loading = countries ? false : hookResult.loading;
  const error = countries ? null : hookResult.error;

  const total = useMemo(() => {
    if (!sourceData) return 0;
    return Object.values(sourceData).reduce((sum, v) => sum + v, 0);
  }, [sourceData]);

  const items = useMemo(() => {
    if (!sourceData) return [];
    return Object.entries(sourceData)
      .map(([country, count]) => ({ country, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
      .map((t, i) => ({
        country: t.country,
        count: t.count,
        pct: total ? (t.count / total) * 100 : 0,
        color: COLORS[i % COLORS.length],
      }));
  }, [sourceData, total]);

  if (!loading && !error && items.length === 0) return null;

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="shrink-0 pb-2">
        <CardTitle className="text-base md:text-lg flex items-center gap-2">
          <Flag className="h-4 w-4 text-primary" />
          Top Countries
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col space-y-3 min-h-0">
        {loading && (
          <div className="h-6 flex items-center">
            <div className="animate-pulse text-sm text-muted-foreground">Loading countries...</div>
          </div>
        )}
        {!loading && error && <div className="text-sm text-muted-foreground">{error}</div>}
        {!loading && !error && items.length > 0 && (
          <>
            {/* Ranked list — fills remaining vertical space */}
            <ul className="flex-1 divide-y overflow-auto">
              {items.map(({ country, count, pct, color }, i) => (
                <li key={country} className="flex items-center gap-2 py-1.5 text-sm">
                  <span className="w-5 shrink-0 text-right text-xs font-medium text-muted-foreground">
                    {i + 1}
                  </span>
                  <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${color}`} />
                  <span className="flex-1 truncate">{country}</span>
                  <span className="font-medium tabular-nums">{count}</span>
                  <span className="w-12 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                    {pct.toFixed(1)}%
                  </span>
                </li>
              ))}
            </ul>

            {/* Stacked bar at bottom */}
            <div className="flex h-3 md:h-4 shrink-0 rounded-md overflow-hidden">
              {items.map(({ country, count, pct, color }) => {
                if (pct === 0) return null;
                return (
                  <div
                    key={country}
                    className={`${color} transition-all duration-500`}
                    style={{ width: `${pct}%` }}
                    title={`${country}: ${count}`}
                  />
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
